<template>
  <el-dialog
    title="查看采收记录"
    :visible.sync="visible"
    width="1100px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
  >
    <div class="order-view-content">
      <!-- 基本信息 -->
      <div class="info-section">
        <h4 class="section-title">基本信息</h4>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <label>采收日期：</label>
              <span>{{ formatDate(form.harvestDate) }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>基地/大棚：</label>
              <span>{{ getGreenhouseName(form.greenhouseId) }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>草莓品种：</label>
              <span>{{ form.varietyName || "-" }}</span>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="20" style="margin-top: 16px">
          <el-col :span="8">
            <div class="info-item">
              <label>采收负责人：</label>
              <span>{{ form.harvestManager }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>采收总量(kg)：</label>
              <span>{{ form.sumWeight || "-" }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>仓储位置：</label>
              <span>{{ form.basicStorageName || "-" }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>品控员：</label>
              <span>{{ form.reviewer || "-" }}</span>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 状态信息 -->
      <div class="info-section" style="margin-top: 24px">
        <h4 class="section-title">状态信息</h4>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <label>状态：</label>
              <el-tag :type="getStatusType(form.status)">{{
                getStatusLabel(form.status)
              }}</el-tag>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>创建时间：</label>
              <span>{{ formatDateTime(form.createTime) }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>更新时间：</label>
              <span>{{ formatDateTime(form.updateTime) }}</span>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 采收图片 -->
      <div
        class="info-section"
        style="margin-top: 24px"
        v-if="form.attachment && form.attachment.length > 0"
      >
        <h4 class="section-title">采收图片</h4>
        <div class="image-gallery">
          <div
            v-for="(image, index) in form.attachment"
            :key="index"
            class="image-item"
          >
            <el-image
              :src="image.url || image"
              :alt="'采收图片' + (index + 1)"
              :preview-src-list="getPreviewSrcList()"
              fit="cover"
              style="width: 100%; height: 100%"
            />
          </div>
        </div>
      </div>
    </div>
    <el-form :model="form" ref="orderForm" label-width="120px">
      <el-form-item
        label="审核记录"
        v-if="Array.isArray(form.reviewLogList) && form.reviewLogList.length"
      >
        <el-table
          :data="form.reviewLogList"
          border
          size="small"
          style="width: 100%"
        >
          <el-table-column prop="reviewer" label="审核人" width="160" />
          <el-table-column prop="reviewMsg" label="审核意见" />
          <el-table-column prop="reviewTime" label="审核时间" width="160">
            <template slot-scope="{ row }">{{
              formatDate(row.reviewTime)
            }}</template>
          </el-table-column>
          <el-table-column prop="packaged" label="交付包装" width="100">
            <template slot-scope="{ row }">{{
              row.packaged ? "是" : "否"
            }}</template>
          </el-table-column>
          <el-table-column prop="deliveryDate" label="交付日期" width="120">
            <template slot-scope="{ row }">{{
              formatDate(row.deliveryDate)
            }}</template>
          </el-table-column>
        </el-table>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import axios from "@/axios";
import { baseUrl } from "@/axios/baseUrl";
import dayjs from "dayjs";

export default {
  name: "OrderViewDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
    greenhouseOptions: {
      type: Array,
      default: () => [],
    },
    harvestManagerOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        harvestDate: null,
        greenhouseId: "",
        varietyId: "",
        harvestManagerId: "",
        sumWeight: null,
        basicStorageId: "",
        attachment: [],
        status: "",
        createTime: null,
        updateTime: null,
      },
    };
  },
  watch: {
    visible(val) {
      if (val && this.orderId) {
        this.fetchOrderDetail();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        harvestDate: null,
        greenhouseId: "",
        varietyId: "",
        harvestManagerId: "",
        sumWeight: null,
        basicStorageId: "",
        attachment: [],
        status: "",
        createTime: null,
        updateTime: null,
      };
    },
    async fetchOrderDetail() {
      try {
        const res = await axios.post(
          baseUrl + `/api/digital-farming/harvest/order-detail/${this.orderId}`
        );
        if (res && res.code === 200 && res.data) {
          const data = res.data;
          this.form = {
            ...data,
            harvestDate: data.harvestDate
              ? dayjs(data.harvestDate).toDate()
              : null,
            attachment: Array.isArray(data.attachment) ? data.attachment : [],
          };
        }
      } catch (err) {
        console.error("fetchOrderDetail error:", err);
        this.$message &&
          this.$message.error &&
          this.$message.error("获取采收记录详情失败");
      }
    },
    handleClose() {
      this.$emit("update:visible", false);
    },
    // 获取名称的辅助方法
    getGreenhouseName(id) {
      const option = this.greenhouseOptions.find((item) => item.value === id);
      return option ? option.label : "-";
    },
    getStatusType(status) {
      const typeMap = {
        DRAFT: "info",
        PENDING: "warning",
        REVIEWED: "success",
        REJECTED: "danger",
      };
      return typeMap[status] || "info";
    },
    getStatusLabel(status) {
      const labelMap = {
        DRAFT: "草稿",
        PENDING: "待审核",
        REVIEWED: "已审核",
        REJECTED: "已退回",
      };
      return labelMap[status] || status || "-";
    },
    formatDate(date) {
      if (!date) return "-";
      try {
        return dayjs(date).format("YYYY-MM-DD");
      } catch (e) {
        return date;
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "-";
      try {
        return dayjs(dateTime).format("YYYY-MM-DD HH:mm:ss");
      } catch (e) {
        return dateTime;
      }
    },
    getPreviewSrcList() {
      if (!Array.isArray(this.form.attachment)) return [];
      return this.form.attachment.map((img) => img.url || img);
    },
    previewImage(image, index) {
      // 图片预览功能
      const imageList = this.form.attachment.map((img) => img.url || img);
      this.$preview && this.$preview(imageList, index);
    },
  },
};
</script>

<style scoped lang="less">
.order-view-content {
  .info-section {
    margin-bottom: 20px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      padding-bottom: 8px;
    }

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 12px;

      label {
        font-weight: 500;
        color: #606266;
        min-width: 100px;
        margin-right: 8px;
      }

      span {
        word-break: break-word;
      }
    }
  }

  .image-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    .image-item {
      width: 120px;
      height: 120px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      overflow: hidden;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 0 8px rgba(64, 158, 255, 0.3);
      }
    }
  }
}
</style>
